import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePosts from '../../../hooks/community/usePosts';
import { Post } from '../../../types/post';
import SearchBar from '../forms/SearchBar';
import PostWallCard from './PostWallCard';

interface PostsWallProps {
  onEdit: (post: Post) => void;
}

export default function PostsWall({ onEdit }: PostsWallProps): JSX.Element {
  const { t } = useTranslation();
  const posts = usePosts() ?? [];
  const [search, setSearch] = useState('');

  const sortedPosts = useMemo(() => {
    const filtered = posts.filter((post) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const authorName = `${post.author.firstName} ${post.author.lastName}`.toLowerCase();
      return (
        (post.content?.toLowerCase().includes(q) ?? false) ||
        authorName.includes(q) ||
        (post.linkedFeatureData?.title?.toLowerCase().includes(q) ?? false)
      );
    });

    return [...filtered].sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [posts, search]);

  return (
    <div className="mx-auto w-full max-w-2xl">
      <SearchBar
        inputProps={{ placeholder: t('page.communicationOverview.posts.search') }}
        search={search}
        setSearch={setSearch}
        className="mb-4 md:w-80 lg:w-96"
      />
      {sortedPosts.length === 0 ? (
        <p className="py-8 text-center text-sm text-primary-600">
          {t('page.communicationOverview.posts.noResults')}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {sortedPosts.map((post) => (
            <PostWallCard key={post.postId} post={post} onEdit={onEdit} />
          ))}
        </div>
      )}
    </div>
  );
}
